// src/admin/components/Sidebar.jsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users,
  Layers,
  Home,
  Group,
  Wrench,
  ClipboardList,
  BarChart3,
  CalendarCheck,
  CreditCard,
  PhoneCall,
} from "lucide-react";
import { useUser } from "../../UserContext";

const Sidebar = () => {
  const { user } = useUser();
  const { pathname } = useLocation();

  const isActive = (path) => pathname === path;

  const linkClass = (path) =>
    `flex items-center gap-3 px-4 py-2 rounded-md transition-all ${
      isActive(path)
        ? "bg-[#54B0AF] text-white shadow-sm"
        : "text-[#323232] hover:bg-gray-100"
    }`;

  const ROLE_IDS = {
    USER: "fe42ee60-9133-4f0e-b543-740d8697c23b",
    ADMIN: "ac9be322-db89-4beb-854a-46f2c91de374",
    SUPER_ADMIN: "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef",
  };

  const roleName =
    user?.role_name ||
    (user?.user_role === ROLE_IDS.ADMIN
      ? "Admin"
      : user?.user_role === ROLE_IDS.SUPER_ADMIN
        ? "Super Admin"
        : user?.user_role === ROLE_IDS.USER
          ? "User"
          : null);

  const normalizedRole = String(user?.user_role || "").toLowerCase();
  const isSuperAdmin =
    roleName === "Super Admin" || normalizedRole === "super_admin";

  return (
    <aside className="w-64 bg-white shadow-md border-r border-gray-200 flex flex-col p-5">

      {/* Logo / Header */}
      <h1 className="text-display text-[#323232] mb-8">Admin Panel</h1>

      {/* Navigation */}
      <nav className="flex flex-col gap-4 text-[15px] font-gotham">
        <SectionLabel>Overview</SectionLabel>
        <div className="flex flex-col gap-2">
          <Link to="/admin" className={linkClass("/admin")}>
            <Home size={18} /> Dashboard
          </Link>
          <Link to="/admin/analytics" className={linkClass("/admin/analytics")}>
            <BarChart3 size={18} /> Analytics
          </Link>
        </div>

        <SectionLabel>People</SectionLabel>
        <div className="flex flex-col gap-2">
          <Link to="/admin/users" className={linkClass("/admin/users")}>
            <Users size={18} /> Users
          </Link>

          <Link to="/admin/waitlist" className={linkClass("/admin/waitlist")}>
            <ClipboardList size={18} /> Waitlist
          </Link>
        </div>

        <SectionLabel>Programs</SectionLabel>
        <div className="flex flex-col gap-2">
          <Link to="/admin/courses" className={linkClass("/admin/courses")}>
            <Layers size={18} /> Courses
          </Link>

          <Link
            to="/admin/accountability-bookings"
            className={linkClass("/admin/accountability-bookings")}
          >
            <CalendarCheck size={18} /> Accountability
          </Link>

          <Link
            to="/admin/consultation-bookings"
            className={linkClass("/admin/consultation-bookings")}
          >
            <PhoneCall size={18} /> Consultation Calls
          </Link>

          <Link to="/admin/payments" className={linkClass("/admin/payments")}>
            <CreditCard size={18} /> Payments
          </Link>

          <Link to="/admin/cohorts" className={linkClass("/admin/cohorts")}>
            <Group size={18} /> Cohorts
          </Link>
        </div>

        {isSuperAdmin && (
          <>
            <SectionLabel>Operations</SectionLabel>
            <div className="flex flex-col gap-2">
              <Link
                to="/admin/maintenance"
                className={linkClass("/admin/maintenance")}
              >
                <Wrench size={18} /> User Maintenance
              </Link>
            </div>
          </>
        )}
      </nav>

    </aside>
  );
};

export default Sidebar;

const SectionLabel = ({ children }) => (
  <div className="text-xs uppercase tracking-wide text-muted px-2">
    {children}
  </div>
);
